import React, { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Group,
  HoverCard,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { openPortalUrl } from '../../../utils/portalNavigation';
import { pendingWithDisplayName } from '../../../utils/linkedIssueHelpers';
import {
  UNCATEGORIZED_LABEL,
  type IssueCountBucket,
  type OpenCriticalIssueRow,
  type OpenCriticalIssuesAnalytics,
} from '../logic/deriveOpenCriticalIssues';

/** Portal Issues / Hindrances report (same as portal Property Tools report). */
const ISSUES_HINDRANCES_REPORT_PATH = 'reason-for-delay';
/** Portal ticket list — used when opening a single ticket from the list. */
const ISSUES_HINDRANCES_TICKET_PATH = 'requests/dprIssueTrackerProcess';

const CARD_RADIUS = 'sm';
const PANEL_HEIGHT = 280;
const DONUT_SIZE = 120;
const DONUT_HOLE = 74;

const BUCKET_COLORS = [
  '#4C6EF5',
  '#15AABF',
  '#12B886',
  '#FAB005',
  '#FD7E14',
  '#E64980',
  '#7950F2',
  '#868E96',
];

function issueCategoryLabel(issue: OpenCriticalIssueRow): string {
  return issue.category.trim() || UNCATEGORIZED_LABEL;
}

function buildConicGradient(
  buckets: IssueCountBucket[],
  total: number,
  selectedLabel: string | null,
): string {
  if (total <= 0 || buckets.length === 0) {
    return 'var(--mantine-color-gray-3)';
  }
  let cursor = 0;
  const parts: string[] = [];
  buckets.forEach((bucket, index) => {
    const sweep = (bucket.count / total) * 360;
    const start = cursor;
    const end = cursor + sweep;
    cursor = end;
    const base = BUCKET_COLORS[index % BUCKET_COLORS.length];
    const color =
      selectedLabel == null || selectedLabel === bucket.label
        ? base
        : `${base}55`;
    parts.push(`${color} ${start}deg ${end}deg`);
  });
  return `conic-gradient(${parts.join(', ')})`;
}

/** Map click on a CSS conic-gradient (starts at 12 o'clock, clockwise) to a bucket label. */
function bucketLabelAtPoint(
  buckets: IssueCountBucket[],
  total: number,
  clientX: number,
  clientY: number,
  rect: DOMRect,
): string | null {
  if (total <= 0 || buckets.length === 0) return null;
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const dx = clientX - cx;
  const dy = clientY - cy;
  const radius = Math.sqrt(dx * dx + dy * dy);
  const outer = Math.min(rect.width, rect.height) / 2;
  const inner = (DONUT_HOLE / DONUT_SIZE) * outer;
  if (radius < inner || radius > outer) return null;

  // atan2: 0 at +x (3 o'clock). Shift so 0 is at top, clockwise.
  let deg = (Math.atan2(dy, dx) * 180) / Math.PI;
  deg = (deg + 90 + 360) % 360;

  let cursor = 0;
  for (const bucket of buckets) {
    const sweep = (bucket.count / total) * 360;
    if (deg >= cursor && deg < cursor + sweep) {
      return bucket.label;
    }
    cursor += sweep;
  }
  // Exact 360° edge falls on last bucket.
  return buckets[buckets.length - 1]?.label ?? null;
}

function CategoryDonut({
  buckets,
  total,
  selectedCategory,
  onToggleCategory,
}: {
  buckets: IssueCountBucket[];
  total: number;
  selectedCategory: string | null;
  onToggleCategory: (label: string) => void;
}) {
  const ringBackground = buildConicGradient(buckets, total, selectedCategory);

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        By category
      </Text>
      <Box style={{ display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
        <Box
          role="img"
          aria-label="Open critical issues by category. Click a segment to filter."
          onClick={(e) => {
            const target = e.currentTarget;
            const label = bucketLabelAtPoint(
              buckets,
              total,
              e.clientX,
              e.clientY,
              target.getBoundingClientRect(),
            );
            if (label) onToggleCategory(label);
          }}
          style={{
            position: 'relative',
            width: DONUT_SIZE,
            height: DONUT_SIZE,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <Box
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              background: ringBackground,
            }}
          />
          <Box
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: DONUT_HOLE,
              height: DONUT_HOLE,
              borderRadius: '50%',
              background: 'var(--mantine-color-body)',
              border: '1px solid var(--mantine-color-default-border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <Text size="sm" fw={600}>
              {total}
            </Text>
          </Box>
        </Box>
      </Box>
      <Stack gap={6}>
        {buckets.length === 0 ? (
          <Text size="sm" c="dimmed">
            No data.
          </Text>
        ) : (
          buckets.map((bucket, index) => {
            const active = selectedCategory === bucket.label;
            const muted = selectedCategory != null && !active;
            return (
              <UnstyledButton
                key={bucket.label}
                onClick={() => onToggleCategory(bucket.label)}
                style={{
                  width: '100%',
                  borderRadius: 6,
                  padding: '4px 6px',
                  background: active ? 'var(--mantine-color-default-hover)' : 'transparent',
                  opacity: muted ? 0.55 : 1,
                }}
              >
                <Group justify="space-between" wrap="nowrap" gap="sm">
                  <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
                    <Box
                      w={8}
                      h={8}
                      style={{
                        borderRadius: '50%',
                        backgroundColor: BUCKET_COLORS[index % BUCKET_COLORS.length],
                        flexShrink: 0,
                      }}
                    />
                    <Text size="sm" truncate>
                      {bucket.label}
                    </Text>
                  </Group>
                  <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
                    {bucket.count}
                  </Text>
                </Group>
              </UnstyledButton>
            );
          })
        )}
      </Stack>
    </Stack>
  );
}

function IssueList({
  issues,
  selectedCategory,
}: {
  issues: OpenCriticalIssueRow[];
  selectedCategory: string | null;
}) {
  return (
    <Stack gap="sm" style={{ height: '100%', minHeight: 0 }}>
      <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
        <Text size="xs" c="dimmed">
          Issues
        </Text>
        {selectedCategory ? (
          <>
            <Text size="xs" c="dimmed">
              ·
            </Text>
            <Text size="xs" c="dimmed" truncate>
              {selectedCategory}
            </Text>
          </>
        ) : null}
      </Group>
      <Stack gap={6} style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 4 }}>
        {issues.length === 0 ? (
          <Text size="sm" c="dimmed">
            No open critical issues{selectedCategory ? ` in ${selectedCategory}` : ''}.
          </Text>
        ) : (
          issues.map((issue) => (
            <UnstyledButton
              key={issue.ticketId}
              onClick={() =>
                openPortalUrl(ISSUES_HINDRANCES_TICKET_PATH, {
                  query: { ticketId: issue.ticketId },
                })
              }
              style={{
                display: 'block',
                width: '100%',
                borderRadius: 8,
                padding: '8px 10px',
                border: '1px solid var(--mantine-color-default-border)',
                background: 'var(--mantine-color-default)',
                textAlign: 'left',
              }}
            >
              <Group justify="space-between" wrap="nowrap" gap="sm" align="flex-start">
                <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                  <Text size="sm" fw={500} truncate>
                    {issue.title || issue.ticketId}
                  </Text>
                  <Text size="xs" c="dimmed" truncate>
                    Pending with {pendingWithDisplayName(issue.pendingWith)}
                    {issue.status ? ` · ${issue.status}` : ''}
                  </Text>
                </Stack>
                {issue.currentlyAffected ? (
                  <Badge size="xs" color="red" variant="light" style={{ flexShrink: 0 }}>
                    Causing delay
                  </Badge>
                ) : null}
              </Group>
            </UnstyledButton>
          ))
        )}
      </Stack>
    </Stack>
  );
}

export interface OpenCriticalIssuesPanelProps {
  analytics: OpenCriticalIssuesAnalytics;
  loading?: boolean;
}

/**
 * Overview panel: category donut (1) + issue list (2) for open Critical issues.
 */
export function OpenCriticalIssuesPanel({
  analytics,
  loading = false,
}: OpenCriticalIssuesPanelProps) {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const empty = !loading && analytics.total === 0;

  const toggleCategory = (label: string) => {
    setSelectedCategory((prev) => (prev === label ? null : label));
  };

  const visibleIssues = useMemo(() => {
    if (!selectedCategory) return analytics.issues;
    return analytics.issues.filter(
      (issue) => issueCategoryLabel(issue) === selectedCategory,
    );
  }, [analytics.issues, selectedCategory]);

  useEffect(() => {
    if (
      selectedCategory != null &&
      !analytics.byCategory.some((b) => b.label === selectedCategory)
    ) {
      setSelectedCategory(null);
    }
  }, [analytics.byCategory, selectedCategory]);

  return (
    <Card withBorder radius={CARD_RADIUS} p="md">
      <Group justify="space-between" mb="sm" wrap="nowrap" gap="sm">
        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
          <Title order={4}>Open critical issues</Title>
          <HoverCard width={300} shadow="md" withArrow position="bottom" openDelay={150} closeDelay={80}>
            <HoverCard.Target>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label="About Open critical issues"
              >
                <IconInfoCircle size={16} stroke={1.5} />
              </ActionIcon>
            </HoverCard.Target>
            <HoverCard.Dropdown>
              <Text size="sm">
                Unique open Critical-priority tickets linked to schedule tasks. The left chart
                breaks them down by category; the list shows titles, pending with, and a Causing
                delay badge when the ticket is currently affecting the schedule. Click an issue to
                open it in the portal.
              </Text>
            </HoverCard.Dropdown>
          </HoverCard>
          <Badge variant="light" color="red">
            {analytics.total}
          </Badge>
        </Group>
        <Button
          size="xs"
          variant="default"
          style={{ flexShrink: 0 }}
          disabled={analytics.total === 0}
          styles={{
            root: {
              backgroundColor: 'var(--mantine-color-body)',
              color: 'var(--mantine-color-text)',
              border: '1px solid var(--mantine-color-default-border)',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'var(--mantine-color-default-hover)',
              },
            },
          }}
          onClick={() => openPortalUrl(ISSUES_HINDRANCES_REPORT_PATH)}
        >
          All issues / hindrances
        </Button>
      </Group>

      {empty ? (
        <Box
          style={{
            minHeight: PANEL_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: '0 24px',
          }}
        >
          <Text size="sm" c="dimmed" maw={420}>
            No open Critical issues linked to schedule tasks. When Critical priority tickets are
            open on tasks, category breakdown and the issue list appear here.
          </Text>
        </Box>
      ) : (
        <Box
          className="open-critical-issues-split"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 2fr',
            gap: 16,
            minHeight: PANEL_HEIGHT,
            alignItems: 'stretch',
          }}
        >
          {/* Left drives card height as categories grow. */}
          <Box className="open-critical-issues-category-col" style={{ minWidth: 0 }}>
            <CategoryDonut
              buckets={analytics.byCategory}
              total={analytics.total}
              selectedCategory={selectedCategory}
              onToggleCategory={toggleCategory}
            />
          </Box>
          {/* Right matches left height; issues scroll inside. */}
          <Box
            className="open-critical-issues-list-col"
            style={{
              position: 'relative',
              minWidth: 0,
              minHeight: PANEL_HEIGHT,
              borderLeft: '1px solid var(--mantine-color-default-border)',
              paddingLeft: 16,
            }}
          >
            <Box
              style={{
                position: 'absolute',
                inset: 0,
                left: 16,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden',
              }}
            >
              <IssueList issues={visibleIssues} selectedCategory={selectedCategory} />
            </Box>
          </Box>
        </Box>
      )}
    </Card>
  );
}
